import { redondearMoneda } from "./ventas";

const texto = (valor) => String(valor ?? "").trim();
const textoFiscal = (valor) =>
  texto(valor).normalize("NFD").replace(/\p{Diacritic}/gu, "").toUpperCase();
const enteroPositivo = (valor) => {
  const numero = Number(valor);
  return Number.isInteger(numero) && numero > 0 ? numero : 0;
};

export const obtenerCondicionIvaId = (cliente) =>
  enteroPositivo(
    cliente?.CondicionIVAId ??
    cliente?.CondicionIvaId ??
    cliente?.CondicionIVA_Id ??
    cliente?.CondicionIva_Id
  );

export const obtenerDocumentoCliente = (cliente) =>
  texto(cliente?.Documento ?? cliente?.NroDocumento ?? cliente?.CUIT ?? cliente?.Cuit).replace(/\D/g, "");

export const clienteTieneCuit = (cliente, tiposDocumento = []) => {
  const documento = obtenerDocumentoCliente(cliente);
  const tipoId = enteroPositivo(cliente?.TipoDocumentoId ?? cliente?.TipoDocumento_Id);
  const tipoCatalogo = tiposDocumento.find((tipo) => Number(tipo.Id) === tipoId);
  const tipo = textoFiscal(
    cliente?.sTipoDocumento ??
    cliente?.TipoDocumento ??
    tipoCatalogo?.sDescripcion ??
    tipoCatalogo?.Descripcion
  );
  const cuitExplicito = texto(cliente?.CUIT ?? cliente?.Cuit ?? cliente?.sCUIT).replace(/\D/g, "");
  return (tipo.includes("CUIT") && documento.length === 11) || cuitExplicito.length === 11;
};

export const descripcionCondicionIva = (id, condicionesIva = []) => {
  const condicion = condicionesIva.find((item) => Number(item.Id) === Number(id));
  return texto(condicion?.sDescripcion ?? condicion?.Descripcion);
};

export const requiereCuit = (condicion) => {
  const valor = textoFiscal(`${condicion?.sCodigo ?? ""} ${condicion?.sDescripcion ?? condicion?.Descripcion ?? ""}`);
  return valor.includes("MONOTRIBUT") ||
    (valor.includes("RESPONSABLE INSCRIPTO") && !valor.includes("NO INSCRIPTO")) ||
    valor === "RI";
};

export const admiteCompraComoConsumidorFinal = (cliente, condicionesIva = []) => {
  const condicionId = obtenerCondicionIvaId(cliente);
  const condicionCatalogo = condicionesIva.find((item) => Number(item.Id) === condicionId);
  return requiereCuit({
    sCodigo: cliente?.CondicionIVACodigo ?? cliente?.sCondicionIVACodigo ?? condicionCatalogo?.sCodigo,
    sDescripcion:
      cliente?.sCondicionIVA ??
      cliente?.CondicionIVADescripcion ??
      cliente?.CondicionIVA ??
      condicionCatalogo?.sDescripcion ??
      condicionCatalogo?.Descripcion,
  });
};

export const codigoArcaTipoDocumento = (tipo = {}) =>
  texto(
    tipo.CodigoARCA ??
    tipo.CodigoArca ??
    tipo.sCodigoARCA ??
    tipo.sCodigoArca ??
    tipo.CodigoAFIP ??
    tipo.CodigoAfip ??
    tipo.sCodigoAFIP ??
    tipo.sCodigoAfip ??
    tipo.Codigo ??
    tipo.sCodigo
  );

export const esCuitValido = (valor) => {
  const documento = texto(valor).replace(/\D/g, "");
  if (!/^\d{11}$/.test(documento)) return false;
  const pesos = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const suma = pesos.reduce(
    (total, peso, indice) => total + Number(documento[indice]) * peso,
    0
  );
  const resto = 11 - (suma % 11);
  const verificador = resto === 11 ? 0 : resto === 10 ? 9 : resto;
  return verificador === Number(documento[10]);
};

export const validarClienteNuevo = (
  cliente,
  tiposDocumento = [],
  condicionesIva = [],
  { exigirCuit = false, exigirDocumento = true } = {}
) => {
  const errores = {};
  const condicionIvaId = enteroPositivo(cliente?.CondicionIVAId);
  const tipoDocumentoId = enteroPositivo(cliente?.TipoDocumentoId);
  const documento = obtenerDocumentoCliente(cliente);
  const condicion = condicionesIva.find((item) => Number(item.Id) === condicionIvaId);
  const tipoDocumento = tiposDocumento.find((tipo) => Number(tipo.Id) === tipoDocumentoId);
  const documentoParcial = Boolean(documento || tipoDocumentoId);
  const debeTenerDocumento = Boolean(exigirDocumento || exigirCuit || documentoParcial);
  const debeTenerCuit = debeTenerDocumento && (exigirCuit || (condicion && requiereCuit(condicion)));

  if (!texto(cliente?.sDescripcion)) errores.sDescripcion = "Ingrese la razon social o nombre.";
  if (!condicion) {
    errores.CondicionIVAId = "Seleccione una condicion frente al IVA valida.";
  }
  if (debeTenerDocumento && !tipoDocumento) {
    errores.TipoDocumentoId = "Seleccione un tipo de documento del catalogo.";
  }
  if (debeTenerDocumento && !documento) errores.Documento = "Ingrese el numero de documento.";
  if (debeTenerCuit && tipoDocumento && codigoArcaTipoDocumento(tipoDocumento) !== "80") {
    errores.TipoDocumentoId = "Seleccione el tipo de documento CUIT con codigo ARCA 80.";
  }
  if (debeTenerCuit && documento && !esCuitValido(documento)) {
    errores.Documento = "Ingrese un CUIT valido (11 numeros o 13 caracteres con guiones).";
  }

  return { valido: Object.keys(errores).length === 0, errores };
};

export const normalizarClienteSeleccion = (cliente) => ({
  Id: enteroPositivo(cliente?.Id),
  sCodigo: texto(cliente?.sCodigo ?? cliente?.Codigo),
  sDescripcion: texto(cliente?.sDescripcion ?? cliente?.Descripcion),
  CondicionIVAId: obtenerCondicionIvaId(cliente),
  Documento: texto(cliente?.Documento ?? cliente?.NroDocumento ?? cliente?.CUIT ?? cliente?.Cuit),
  TipoDocumentoId: enteroPositivo(cliente?.TipoDocumentoId ?? cliente?.TipoDocumento_Id),
  sDocumento: texto(cliente?.sDocumento ?? cliente?.Documento ?? cliente?.NroDocumento),
  sCondicionIVA: texto(
    cliente?.sCondicionIVA ??
    cliente?.CondicionIVADescripcion ??
    cliente?.CondicionIVA ??
    cliente?.CondicionIva
  ),
  sTipoDocumento: texto(
    cliente?.sTipoDocumento ??
    cliente?.TipoDocumentoDescripcion ??
    cliente?.TipoDocumento
  ),
});

export const normalizarConsumidorFinal = (respuesta = {}) => ({
  ClienteConsumidorFinalId: enteroPositivo(respuesta.ClienteConsumidorFinalId),
  Codigo: texto(respuesta.Codigo),
  Descripcion: texto(respuesta.Descripcion),
  MontoSinIdentificar: redondearMoneda(respuesta.MontoSinIdentificar),
  Error: texto(respuesta.Error),
});

export const normalizarOpcionesFacturacion = (respuesta, clienteId) => {
  const listaCruda = Array.isArray(respuesta?.Lista) ? respuesta.Lista : [];
  const lista = listaCruda
    .map((opcion) => ({
      NumeradorId: enteroPositivo(opcion?.NumeradorId),
      ComprobanteTipoId: enteroPositivo(opcion?.ComprobanteTipoId),
      Codigo: texto(opcion?.Codigo),
      Descripcion: texto(opcion?.Descripcion),
      Letra: texto(opcion?.Letra).toUpperCase(),
      PuntoVenta: texto(opcion?.PuntoVenta),
      ProximoNumero: enteroPositivo(opcion?.ProximoNumero),
      Sugerido: Boolean(opcion?.Sugerido),
    }))
    .filter((opcion) => opcion.NumeradorId > 0);
  const sugeridoRespuesta = enteroPositivo(respuesta?.NumeradorSugeridoId);
  const sugerido = lista.some((opcion) => opcion.NumeradorId === sugeridoRespuesta)
    ? sugeridoRespuesta
    : lista.find((opcion) => opcion.Sugerido)?.NumeradorId || 0;

  return {
    ClienteId: enteroPositivo(respuesta?.ClienteId) || enteroPositivo(clienteId),
    ClienteConsumidorFinalId: enteroPositivo(respuesta?.ClienteConsumidorFinalId),
    EsConsumidorFinalGenerico: Boolean(respuesta?.EsConsumidorFinalGenerico),
    MontoConsumidorFinalSinIdentificar: redondearMoneda(respuesta?.MontoConsumidorFinalSinIdentificar),
    RequiereIdentificacionFiscal: Boolean(respuesta?.RequiereIdentificacionFiscal),
    FacturarComoConsumidorFinal: Boolean(respuesta?.FacturarComoConsumidorFinal),
    CondicionIVAId: enteroPositivo(respuesta?.CondicionIVAId),
    CondicionIVACodigo: texto(respuesta?.CondicionIVACodigo),
    CondicionIVA: texto(respuesta?.CondicionIVA),
    NumeradorSugeridoId: sugerido,
    Lista: lista,
    Error: texto(respuesta?.Error),
  };
};

export const crearFacturacionVenta = ({
  NumeradorId,
  SolicitaIdentificacionFiscal = false,
  FacturarComoConsumidorFinal = false,
  FormaPagoId = 0,
  CuentaCorrienteId = 0,
  FechaFactura = null,
  FechaVencimiento = null,
}) => ({
  GenerarFacturaLocal: true,
  SolicitaIdentificacionFiscal: Boolean(SolicitaIdentificacionFiscal),
  FacturarComoConsumidorFinal: Boolean(FacturarComoConsumidorFinal),
  NumeradorId: enteroPositivo(NumeradorId),
  FormaPagoId: enteroPositivo(FormaPagoId),
  CuentaCorrienteId: enteroPositivo(CuentaCorrienteId),
  FechaFactura,
  FechaVencimiento,
  lstImpuestos: [],
});

export const puedeEditarIvaLinea = (linea) => linea?.TipoLinea === "Libre";

const aCentavos = (valor) => Math.round((Number(valor) || 0) * 100);

export const analizarComprobacionFiscal = (respuesta = {}) => {
  const factura = respuesta.Factura || null;
  const detalle = Array.isArray(respuesta.Detalle) ? respuesta.Detalle : [];
  const grupos = new Map();

  detalle.forEach((linea) => {
    const porcentaje = Number(linea.PorcentajeIva) || 0;
    const clave = porcentaje.toFixed(4);
    const actual = grupos.get(clave) || { Porcentaje: porcentaje, NetoCentavos: 0, IvaCentavos: 0, TotalCentavos: 0 };
    actual.NetoCentavos += aCentavos(linea.Neto);
    actual.IvaCentavos += aCentavos(linea.Iva);
    actual.TotalCentavos += aCentavos(linea.Total);
    grupos.set(clave, actual);
  });

  const alicuotas = [...grupos.values()]
    .sort((a, b) => a.Porcentaje - b.Porcentaje)
    .map((grupo) => ({
      Porcentaje: grupo.Porcentaje,
      Neto: grupo.NetoCentavos / 100,
      Iva: grupo.IvaCentavos / 100,
      Total: grupo.TotalCentavos / 100,
    }));
  const diferenciaCentavos = factura
    ? aCentavos(factura.Neto) + aCentavos(factura.Iva) + aCentavos(factura.Tributos) - aCentavos(factura.Total)
    : 0;

  return {
    Factura: factura,
    Detalle: detalle,
    AlicuotasIVA: alicuotas,
    Diferencia: redondearMoneda(diferenciaCentavos / 100),
    DiferenciaCentavos: diferenciaCentavos,
    DiferenciaSignificativa: Math.abs(diferenciaCentavos) > 1,
    Error: texto(respuesta.Error),
  };
};
